import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, FileText, AlertTriangle, Users } from 'lucide-react';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <div className="h-6 w-px bg-white/20"></div>
            <h1 className="text-xl font-brand font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              Terms of Service
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <Card className="glass-premium">
          <CardContent className="p-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Terms of Service</h1>
                <p className="text-white/70">Legal Agreement & User Guidelines</p>
              </div>
            </div>

            <div className="prose prose-invert max-w-none space-y-8 text-white/80 leading-relaxed">
              <section>
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-6 mb-6">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-300 mb-2">Important Notice</h3>
                      <p className="text-yellow-200/80">
                        By accessing usexxx, you agree to be bound by these Terms of Service. 
                        Please read carefully before using our services.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                <p>
                  By accessing and using usexxx ("the Service"), you accept and agree to be bound by the terms 
                  and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Users className="h-5 w-5 mr-2 text-blue-400" />
                  2. Age Verification & Eligibility
                </h2>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <ul className="list-disc list-inside space-y-2">
                    <li><strong>You must be 18 years of age or older</strong> to access this website</li>
                    <li>You have the legal right to access adult material in your jurisdiction</li>
                    <li>You are accessing this website for personal, non-commercial use</li>
                    <li>You will not allow minors to access this website through your account</li>
                  </ul>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. Prohibited Activities</h2>
                <p>You agree not to engage in any of the following prohibited activities:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Copying, distributing, or disclosing any part of the service</li>
                  <li>Using the service for any commercial purpose without authorization</li>
                  <li>Attempting to access restricted areas of the website</li>
                  <li>Uploading malicious code or attempting to hack the service</li>
                  <li>Creating multiple accounts or sharing account credentials</li>
                  <li>Violating any applicable local, state, or international laws</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. Content & Copyright</h2>
                <p>
                  All content on usexxx is protected by copyright and other intellectual property laws. 
                  Users may not copy, distribute, modify, or create derivative works from any content without explicit permission.
                </p>
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
                  <p className="text-blue-300">
                    <strong>DMCA Compliance:</strong> We respect copyright holders' rights and respond to valid DMCA takedown notices. 
                    See our DMCA policy for more information.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Privacy & Data Protection</h2>
                <p>
                  Your privacy is important to us. Please review our Privacy Policy to understand how we collect, 
                  use, and protect your personal information.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>We use cookies to enhance your browsing experience</li>
                  <li>We do not sell personal information to third parties</li>
                  <li>All data is encrypted and securely stored</li>
                  <li>You can request data deletion at any time</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Service Availability</h2>
                <p>
                  usexxx strives to provide reliable service but cannot guarantee 100% uptime. 
                  We reserve the right to modify, suspend, or discontinue any part of the service with or without notice.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Limitation of Liability</h2>
                <div className="bg-gray-500/10 border border-gray-500/20 rounded-lg p-6">
                  <p>
                    usexxx shall not be liable for any indirect, incidental, special, consequential, or punitive damages 
                    resulting from your use of the service. Our total liability is limited to the amount you paid for the service, if any.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Termination</h2>
                <p>
                  We may terminate or suspend your access to the service immediately, without prior notice, for any reason, 
                  including breach of these Terms of Service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. Contact Information</h2>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <p className="text-green-300">
                    <strong>Questions about these Terms?</strong><br />
                    Email: legal@usexxx.com<br />
                    Response time: 24-48 hours
                  </p>
                </div>
              </section>

              <div className="border-t border-white/10 pt-8 mt-12">
                <p className="text-sm text-white/60">
                  <strong>Last Updated:</strong> January 2024<br />
                  <strong>Effective Date:</strong> Immediately upon posting<br />
                  <strong>Changes:</strong> We may update these terms at any time. Continued use constitutes acceptance of new terms.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsPage;